import {
  AuthenticationResult,
  EventType,
  InteractionRequiredAuthError,
  IPublicClientApplication,
  PublicClientApplication,
  SilentRequest,
} from "@azure/msal-browser";
import { ApiScope, msalConfig } from "@config/msal";

export const msalInstance = new PublicClientApplication(msalConfig);

// The popup flow sets the active account explicitly (loginWithPopup); the
// redirect flow comes back through MsalProvider's handleRedirectPromise,
// where only this event callback can set it.
msalInstance.addEventCallback(event => {
  if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
    msalInstance.setActiveAccount((event.payload as AuthenticationResult).account);
  }
});

export const isMobile = (): boolean => /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

export const loginRequest = {
  scopes: ["openid"],
  extraScopesToConsent: [ApiScope],
};

/**
 * Thrown when the session requires interactive re-authentication and the
 * interactive fallback could not complete (popup blocked or dismissed).
 * Unlike a transient network failure, retrying cannot succeed: the user
 * must go through login again.
 */
export class ReauthRequiredError extends Error {
  constructor(public readonly cause: unknown) {
    super("REAUTH_REQUIRED");
    this.name = "ReauthRequiredError";
  }
}

/**
 * Get access token for API calls with automatic refresh.
 * Falls back to interactive popup if silent acquisition fails.
 *
 * @returns AuthenticationResult with accessToken and account info
 * @throws ReauthRequiredError if re-authentication is needed but the popup
 * fallback fails, any other Error for transient failures
 */

export const getMicrosoftToken = async (): Promise<AuthenticationResult> => {
  const request: SilentRequest = {
    scopes: [ApiScope],
  };

  try {
    return await msalInstance.acquireTokenSilent(request);
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      console.log('Silent "acquire token" failed, falling back to popup', error);
      try {
        return await msalInstance.acquireTokenPopup(request);
      } catch (popupError) {
        throw new ReauthRequiredError(popupError);
      }
    }
    throw error;
  }
};

/**
 * Authenticate user with Microsoft SSO using popup flow.
 * Requests OpenID scope and API access, then sets the active account.
 *
 * @param instance - MSAL PublicClientApplication instance
 * @returns AuthenticationResult with accessToken and account info
 * @throws Error if login fails
 */
export const loginWithPopup = async (
  instance: IPublicClientApplication
): Promise<AuthenticationResult> => {
  const result = await instance.loginPopup(loginRequest);

  instance.setActiveAccount(result.account);
  return result;
};

/**
 * Authenticate user with Microsoft SSO using redirect flow.
 * Used on mobile where popups are unreliable (blocked nested popups, lost
 * hash fragments). Navigates away from the app; completion is handled by
 * MsalProvider through handleRedirectPromise on the post-redirect page load.
 *
 * @param instance - MSAL PublicClientApplication instance
 */
export const loginWithRedirect = async (instance: IPublicClientApplication): Promise<void> => {
  await instance.loginRedirect(loginRequest);
};

export const isMsalError = (error: unknown, errorCode: string): boolean => {
  return (
    typeof error === "object" &&
    error !== null &&
    "errorCode" in error &&
    error.errorCode === errorCode
  );
};

export const getMsalErrorMessage = (error: unknown): string => {
  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof error.message === "string"
  ) {
    return error.message;
  }
  return "Erreur de connexion inconnue";
};
